import { ArticleType } from '@buildwise/pages/knowledge/hooks/useArticlesQuery';
import { Badge, Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import Avatar from 'components/common/avatar/Avatar';
import { theme } from 'config/theme';
import { USER_PROFILE, VIEW_ARTICLE } from 'constants/routes';
import { Link } from 'react-router-dom';
import FileEditLineIcon from 'remixicon-react/FileEditFillIcon';

type RenderArticleActivityProps = {
  article: ArticleType;
};

function RenderArticleActivity({ article }: RenderArticleActivityProps) {
  return (
    <Container key={article.id}>
      <Box mr={2}>
        <Badge
          overlap="circular"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          sx={{ zIndex: 0 }}
          badgeContent={
            <BadgeContent>
              <FileEditLineIcon color={theme.palette.custom.white} />
            </BadgeContent>
          }
        >
          <Avatar
            src={article?.profileImageURL}
            size={44}
            name={article?.userNameToDisplay}
            to={USER_PROFILE.replace(':userName', encodeURIComponent(article?.createdBy))}
          />
        </Badge>
      </Box>
      <Box display="flex" flexDirection="column" flex={1} overflow="hidden">
        <ArticleLink to={`${VIEW_ARTICLE.replace(':articleId', article?.id)}`}>
          {article?.title}
        </ArticleLink>
        <ArticleInfo>
          <UserLink to={USER_PROFILE.replace(':userName', encodeURIComponent(article?.createdBy))}>
            {article?.userNameToDisplay}
          </UserLink>{' '}
          <Typography fontSize={14} lineHeight={1.2}>
            {article.userNameTitleToDisplay}
          </Typography>
        </ArticleInfo>
      </Box>
    </Container>
  );
}

const Container = styled(Box, { label: 'Container' })(({ theme }) => ({
  display: 'flex',
  padding: 12,
  marginBottom: 8,
  borderRadius: 4,
  backgroundColor: theme.palette.custom.alabaster,
  overflow: 'hidden',
  '&:last-child': {
    border: 'none',
  },
}));

const BadgeContent = styled(Box, { label: 'BadgeContent' })(({ theme }) => ({
  svg: {
    backgroundColor: theme.palette.custom.link,
    borderRadius: '50%',
    width: 20,
    height: 20,
    padding: 4,
    path: {
      color: theme.palette.custom.white,
    },
  },
}));

const ArticleLink = styled(Link, { label: 'ArticleLink' })(({ theme }) => ({
  width: '100%',
  fontWeight: 700,
  color: theme.palette.custom.activeTitle,
  '&:hover': {
    textDecoration: 'none',
    color: theme.palette.custom.link,
  },
}));

const ArticleInfo = styled(Typography, { label: 'ArticleInfo' })(({ theme }) => ({
  color: theme.palette.custom.slate,
  fontSize: 14,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

const UserLink = styled(Link, { label: 'UserLink' })(({ theme }) => ({
  color: theme.palette.custom.slate,
  fontWeight: 600,
  fontSize: 14,
  lineHeight: 1.2,
  '&:hover': {
    textDecoration: 'underline',
    color: theme.palette.custom.link,
  },
}));

export default RenderArticleActivity;
